import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { isBefore, startOfDay } from 'date-fns';
import Decimal from 'decimal.js';
import { RefundStatusTypeEnumDTO } from 'anchor-ts-infra/src/dto/payment/dto';
import { InvoiceDisplayStatus, InvoicePaymentDisplayStatusType } from 'anchor-scout-common/types';
import { InvoiceAccountingIntegration } from '../InvoiceAccountingIntegration';
import { InvoiceIntegrationModel, InvoiceIntegrationStatus } from '../InvoiceIntegrationModel';
import { InvoiceStatus } from './model';
export function setVoided(invoice, userId) {
    return _object_spread_props(_object_spread({}, invoice), {
        status: {
            type: InvoiceStatus.Voided,
            voided: {
                userId
            }
        },
        displayStatus: InvoiceDisplayStatus.Voided,
        paymentDisplayStatus: {
            type: InvoicePaymentDisplayStatusType.None
        }
    });
}
export function setRefundRequested(invoice, userId, reason) {
    return _object_spread_props(_object_spread({}, invoice), {
        paymentDisplayStatus: {
            type: InvoicePaymentDisplayStatusType.RefundRequested,
            userId,
            reason
        }
    });
}
export function setRefunded(invoice, timestamp, now) {
    return _object_spread_props(_object_spread({}, invoice), {
        status: {
            type: InvoiceStatus.Refunded,
            refunded: {
                timestamp
            }
        },
        displayStatus: InvoiceDisplayStatus.Refunded,
        paymentDisplayStatus: {
            type: InvoicePaymentDisplayStatusType.Refunded,
            timestamp
        },
        xeroIntegration: new InvoiceAccountingIntegration(new InvoiceIntegrationModel({
            status: InvoiceIntegrationStatus.RefundRecorded,
            date: now.getTime()
        }), invoice.xeroIntegration.isSynced()),
        integration: new InvoiceIntegrationModel({
            status: InvoiceIntegrationStatus.RefundRecorded,
            date: now.getTime()
        })
    });
}
export function setPartialRefundRequested(param) {
    let { invoice, userId, reason, refundAmount: _refundAmount, refundFees: _refundFees } = param;
    return _object_spread_props(_object_spread({}, invoice), {
        paymentDisplayStatus: {
            type: InvoicePaymentDisplayStatusType.RefundRequested,
            userId,
            reason
        }
    });
}
export function setPartiallyRefunded(invoice, refund) {
    var _invoice_payment, _payment_creditCardFees;
    const refunds = ((_invoice_payment = invoice.payment) === null || _invoice_payment === void 0 ? void 0 : _invoice_payment.refunds()) || [];
    const hasRefunds = refunds.length > 0;
    const lastRefund = refunds[refunds.length - 1];
    const payment = invoice.payment;
    const creditCardFeesAmount = payment && ((_payment_creditCardFees = payment.creditCardFees()) === null || _payment_creditCardFees === void 0 ? void 0 : _payment_creditCardFees.feesAmount);
    const refundNumber = hasRefunds ? lastRefund.amounts.refundNumber + 1 : 1;
    const refundAmount = new Decimal(refund.refundAmount);
    const remainingAmount = hasRefunds ? new Decimal(lastRefund.amounts.remainingAmount).sub(refundAmount.mul(100)) : refund.rawTotalAmount.sub(refundAmount).mul(100);
    const feesRatio = refundAmount.div(refund.rawTotalAmount);
    const refundFees = refund.refundFees && creditCardFeesAmount ? new Decimal(creditCardFeesAmount).mul(feesRatio) : new Decimal(0);
    const remainingFees = refund.refundFees && creditCardFeesAmount ? hasRefunds ? new Decimal(lastRefund.amounts.remainingFees).sub(refundFees.mul(100)) : new Decimal(creditCardFeesAmount).sub(refundFees) : new Decimal(0);
    const newRefund = {
        refundStatus: RefundStatusTypeEnumDTO.Refunded,
        refundFailed: undefined,
        timestamp: refund.timestamp,
        reason: refund.reason,
        userId: refund.userId,
        refundId: undefined,
        amounts: {
            refundAmount: refundAmount.mul(100).toNumber(),
            refundFees: refundFees.mul(100).toNumber(),
            remainingAmount: remainingAmount.toNumber(),
            remainingFees: remainingFees.toNumber(),
            totalRefundAmount: refundAmount.add(refundFees).mul(100).toNumber(),
            refundNumber
        }
    };
    return _object_spread(_object_spread_props(_object_spread({}, invoice), {
        status: {
            type: InvoiceStatus.PartiallyRefunded,
            partiallyRefunded: {
                timestamp: refund.timestamp
            }
        },
        displayStatus: InvoiceDisplayStatus.PartiallyRefunded,
        paymentDisplayStatus: {
            type: InvoicePaymentDisplayStatusType.Refunded,
            timestamp: refund.timestamp
        }
    }), invoice.payment ? {
        payment: invoice.payment.addRefund(newRefund)
    } : {});
}
export function setPending(invoice, now) {
    return _object_spread_props(_object_spread({}, invoice), {
        status: {
            type: InvoiceStatus.Pending
        },
        displayStatus: isBefore(startOfDay(invoice.rawDueDate), startOfDay(now)) ? InvoiceDisplayStatus.Overdue : InvoiceDisplayStatus.Issued,
        paymentDisplayStatus: {
            type: InvoicePaymentDisplayStatusType.None
        }
    });
}
export function setPaymentConfirmed(invoice, isVendor) {
    return _object_spread_props(_object_spread({}, invoice), {
        status: {
            type: InvoiceStatus.PaymentConfirmed
        },
        displayStatus: InvoiceDisplayStatus.PaymentInitiated,
        paymentDisplayStatus: isVendor ? {
            type: InvoicePaymentDisplayStatusType.Collected
        } : {
            type: InvoicePaymentDisplayStatusType.None
        }
    });
}
export function setIntegration(invoice, integration) {
    return _object_spread_props(_object_spread({}, invoice), {
        integration
    });
}
export function setXeroIntegration(invoice, integration) {
    const xeroIntegration = new InvoiceAccountingIntegration(integration, invoice.xeroIntegration.isSynced());
    return _object_spread_props(_object_spread({}, invoice), {
        xeroIntegration
    });
}
export function setApprovalMethod(invoice, approvalMethod) {
    if (invoice.status.type === InvoiceStatus.RequiresPaymentApproval) {
        return _object_spread_props(_object_spread({}, invoice), {
            status: _object_spread_props(_object_spread({}, invoice.status), {
                requiresPaymentApproval: _object_spread_props(_object_spread({}, invoice.status.requiresPaymentApproval), {
                    approvalMethod
                })
            })
        });
    }
    return invoice;
}
